import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import DocumentUpload from './DocumentUpload';
// Import necessary icons
import { BiFile, BiArrowBack, BiMap, BiBriefcase, BiInfoCircle, BiBullseye, BiX, BiUser, BiDownload } from 'react-icons/bi'; 

import { API_BASE_URL } from '../utils/urls.js';

// --- Reusable Component for Info Boxes ---
const DetailBox = ({ title, children, className = '' }) => (
    <div className={`bg-white border-l-4 border-blue-500 rounded-lg p-5 space-y-3 shadow-md ${className}`}>
        <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-3 flex items-center space-x-2">
            {title.includes('Servee') ? <BiUser className="w-5 h-5 text-blue-500" /> : <BiBriefcase className="w-5 h-5 text-blue-500" />}
            <span>{title}</span>
        </h3>
        {children}
    </div>
);

const DetailItem = ({ label, value }) => {
    if (!value) return null;
    
    const displayValue = Array.isArray(value) || (typeof value === 'string' && value.includes(','))
        ? value.toString().split(',').map((v, i) => 
            <span key={i} className="inline-block text-gray-800 text-sm font-normal mr-2">{v.trim()}</span>
        )
        : value;

    return (
        <p className="text-sm text-gray-800 mb-2">
            <span className="font-semibold text-gray-600 mr-2 min-w-[150px] inline-block">{label}:</span>
            <span className="font-medium text-gray-900">{displayValue}</span>
        </p>
    );
};

// ====================================================================
// --- NEW COMPONENT: RecordServiceAttemptModal ---
// ====================================================================

const ServiceAttemptRecorder = ({ caseId, servees, onRecordSuccess, onClose }) => {
    const [statusType, setStatusType] = useState('');
    const [notesOutcome, setNotesOutcome] = useState('');
    const [serveeId, setServeeId] = useState('');
    const [geoStatus, setGeoStatus] = useState('Awaiting Location...');
    const [location, setLocation] = useState({ latitude: null, longitude: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const user = JSON.parse(localStorage.getItem('user'));
    const token = localStorage.getItem('token');

    // 1. Get Real-time GPS Location on load
    useEffect(() => {
        if (!navigator.geolocation) {
            setGeoStatus('Geolocation not supported by this browser.');
            return;
        }

        const geoOptions = { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 };

        navigator.geolocation.getCurrentPosition((position) => {
            setLocation({
                latitude: position.coords.latitude.toFixed(6),
                longitude: position.coords.longitude.toFixed(6),
            });
            setGeoStatus('Location Captured (High Accuracy)');
        }, (error) => {
            setGeoStatus(`Location Error: ${error.message}. Please move and try again.`);
        }, geoOptions);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        if (!location.latitude || !serveeId) {
            setSubmitError('Location data or Servee selection is missing.');
            return;
        }

        setIsSubmitting(true);
        
        // Note: photo_proof_path and signature_image_path should be linked via DocumentUpload
        // For simplicity here, we assume they'll be uploaded separately and referenced, or the Field Agent handles file upload immediately after check-in.
        
        try {
            const payload = {
                servee_id: serveeId,
                status_type: statusType,
                notes_outcome: notesOutcome,
                latitude: location.latitude,
                longitude: location.longitude,
                // These fields assume file paths are handled/uploaded separately by the agent or a preceding step:
                photo_proof_path: null, 
                signature_image_path: null,
            };

            await axios.post(`${API_BASE_URL}/cases/${caseId}/attempt`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            onRecordSuccess(); // Refresh case data
            onClose(); // Close modal

        } catch (err) {
            console.error('Service attempt submission failed:', err);
            setSubmitError(err.response?.data?.message || 'Failed to record attempt.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="relative bg-white rounded-lg shadow-xl p-8 w-full max-w-lg mx-4">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">
                    <BiX className="w-6 h-6" />
                </button>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                    <BiBullseye className="w-6 h-6 text-blue-600" />
                    <span>Record Service Attempt</span>
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* GPS Status */}
                    <div className="p-3 border rounded-lg bg-blue-50">
                        <p className="text-sm font-medium text-blue-700">GPS Location Status:</p>
                        <p className={`text-sm font-semibold ${location.latitude ? 'text-green-600' : 'text-yellow-600'}`}>
                            {geoStatus}
                        </p>
                        {location.latitude && (
                            <p className="text-xs text-blue-800 mt-1">
                                Lat: {location.latitude}, Long: {location.longitude}
                            </p>
                        )}
                    </div>
                    
                    {/* Servee Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700" htmlFor="servee-select">Servee Target</label>
                        <select 
                            id="servee-select" 
                            value={serveeId} 
                            onChange={(e) => setServeeId(e.target.value)} 
                            required 
                            disabled={isSubmitting}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">-- Select Servee for this Attempt --</option>
                            {servees.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.full_name} ({s.primary_address})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Status Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700" htmlFor="status-type">Attempt Result</label>
                        <select 
                            id="status-type" 
                            value={statusType} 
                            onChange={(e) => setStatusType(e.target.value)} 
                            required 
                            disabled={isSubmitting}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">-- Select Result --</option>
                            <option value="Served">Served (Personal)</option>
                            <option value="Served_Substitute">Served (Substitute/Posted)</option>
                            <option value="Non-Service_Attempt">Non-Service Attempt (Door Unattended)</option>
                            <option value="Non-Service_Refused">Non-Service (Refused/Hostile)</option>
                            <option value="Non-Service_BadAddress">Non-Service (Bad Address)</option>
                            <option value="Check-In_Observation">Check-In/Observation Only</option>
                        </select>
                    </div>

                    {/* Notes/Outcome */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700" htmlFor="notes">Notes/Narrative (Required for Affidavit)</label>
                        <textarea 
                            id="notes" 
                            value={notesOutcome} 
                            onChange={(e) => setNotesOutcome(e.target.value)} 
                            rows="3" 
                            required 
                            disabled={isSubmitting}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Door was blue, car was a red Ford pickup. Knocked 3 times, waited 5 mins. No response."
                        ></textarea>
                    </div>

                    {submitError && (
                        <p className="text-sm text-red-600 font-medium">{submitError}</p>
                    )}

                    <div className="flex justify-end pt-4 space-x-3">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            disabled={isSubmitting} 
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            disabled={isSubmitting || !location.latitude || !serveeId} 
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition disabled:opacity-50"
                        >
                            {isSubmitting ? 'Recording...' : 'Record Attempt & GPS'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// ====================================================================
// --- MAIN COMPONENT: MiniCaseDetail ---
// ====================================================================

const MiniCaseDetail = () => {
    const { id } = useParams();
    const [caseData, setCaseData] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [media, setMedia] = useState([]);
    const [servees, setServees] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAttemptModal, setShowAttemptModal] = useState(false); // NEW STATE for modal
    
    const user = JSON.parse(localStorage.getItem('user'));
    const roleName = user?.roleName;
    const isFieldAgent = roleName === 'field_agent';
    const canUpload = roleName === 'admin' || roleName === 'case_manager' || roleName === 'field_agent';

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        if (!token) { setError('Authentication token not found.'); setLoading(false); return; }
        try {
            const [caseRes, docsRes, mediaRes, serveesRes, activityRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/cases/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE_URL}/cases/${id}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE_URL}/cases/${id}/media`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE_URL}/cases/${id}/servees`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE_URL}/cases/${id}/activity`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            setCaseData(caseRes.data);
            setDocuments(docsRes.data);
            setMedia(mediaRes.data);
            setServees(serveesRes.data);
            setActivity(activityRes.data);
        } catch (err) {
            console.error('Error fetching case details:', err);
            setError(err.response?.data?.message || 'Failed to load case details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleDownload = async (documentId, fileName, type) => {
        // ... (Download logic remains the same)
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${API_BASE_URL}/cases/download/${type}/${documentId}`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` }
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            const fileExtension = fileName.split('.').pop();
            const mimeType = type === 'documents' ? `application/${fileExtension}` : `${type}/${fileExtension}`;

            const blob = new Blob([response.data], { type: mimeType });

            const linkElement = document.createElement('a');
            linkElement.href = window.URL.createObjectURL(blob);
            linkElement.download = fileName;
            document.body.appendChild(linkElement);
            linkElement.click();
            document.body.removeChild(linkElement);
            window.URL.revokeObjectURL(linkElement.href);

        } catch (err) {
            console.error('Error downloading document:', err);
            alert('Failed to download document.');
        }
    };
    
    const handleActionSuccess = () => {
        fetchData(); // Refresh data after upload or service attempt
    };

    if (loading) { return <div className="p-10 text-center text-lg text-blue-600">Loading case details...</div>; }
    if (error) { return <div className="p-10 text-center text-xl text-red-600">{error}</div>; }
    if (!caseData) { return <div className="p-10 text-center text-lg text-gray-500">Case not found.</div>; }

    const { custom_fields = {} } = caseData;

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            
            {/* --- Control Buttons (Streamlined for Field Agent) --- */}
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
                <Link to="/cases" className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition duration-150 font-bold text-lg">
                    <BiArrowBack className="w-6 h-6" />
                    <span>Back to Assigned Cases</span>
                </Link>
                
                {/* NEW: GPS Check-in Button (Primary action for Field Agent) */}
                {isFieldAgent && servees.length > 0 && (
                    <button
                        onClick={() => setShowAttemptModal(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition duration-300 flex items-center space-x-1"
                    >
                        <BiBullseye className="w-5 h-5" />
                        <span>Record Service Attempt</span>
                    </button>
                )}
            </div>

            {/* --- Case Header (Essential Info) --- */}
            <header className="mb-8 p-6 bg-white shadow-xl rounded-lg border-t-8 border-blue-600">
                <h1 className="text-3xl font-extrabold text-gray-900">{caseData.case_name}</h1>
                <p className="text-xl text-gray-600 mt-1">Case No: <span className="font-mono font-bold text-black">{caseData.case_number}</span></p>
                <div className="mt-4 flex flex-wrap items-center space-x-6">
                    <span className="text-md font-bold text-green-700 bg-green-100 px-3 py-1 rounded-full"><BiInfoCircle className="inline-block mr-1 w-4 h-4" /> Status: {caseData.status}</span>
                    <span className="text-md font-bold text-red-700 bg-red-100 px-3 py-1 rounded-full"><BiMap className="inline-block mr-1 w-4 h-4" /> Due: {caseData.due_date ? new Date(caseData.due_date).toLocaleDateString() : 'N/A'}</span>
                    <span className="text-md font-bold text-purple-700 bg-purple-100 px-3 py-1 rounded-full"><BiBriefcase className="inline-block mr-1 w-4 h-4" /> Service Type: {caseData.service_type}</span>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    
                    {/* --- Section 1: Servee Location Information --- */}
                    <DetailBox title="Defendant/Servee Location & Description">
                        {servees.length > 0 ? (
                            <div className="space-y-6">
                                {servees.map((servee, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-md border border-gray-200">
                                        <h4 className="font-extrabold text-lg text-black mb-3 border-b pb-2">Servee #{index + 1}: {servee.full_name}</h4>
                                        <DetailItem label="Primary Address" value={`${servee.primary_address}${servee.apartment_unit ? ', Unit ' + servee.apartment_unit : ''}, ${servee.city_state_zip}`} />
                                        <DetailItem label="Alternate Address" value={servee.alternate_address} />
                                        <div className="mt-4 pt-3 border-t border-gray-100">
                                            <DetailItem label="Aliases/AKA" value={servee.aliases} />
                                            <DetailItem label="Physical Description" value={servee.physical_description} />
                                            <DetailItem label="Vehicle Description" value={servee.vehicle_description} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No servee location information found.</p>
                        )}
                    </DetailBox>

                    {/* --- Section 2: Service Instructions & Notes --- */}
                    <DetailBox title="Service Instructions & Case Notes">
                        <div className="space-y-4">
                            <h4 className="font-bold text-md text-black mb-2 border-b pb-1">Required Documents & Logistics:</h4>
                            <DetailItem label="Documents To Be Served" value={caseData.document_served || (documents.find(d => d.documents_to_be_served)?.documents_to_be_served) || 'N/A'} />
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <DetailItem label="Service Instructions" value={caseData.service_instructions || (documents.find(d => d.service_instructions)?.service_instructions) || 'N/A'} />
                                <DetailItem label="Special Notes" value={caseData.special_instructions || (documents.find(d => d.special_instructions)?.special_instructions) || 'N/A'} />
                            </div>
                        </div>
                    </DetailBox>
                    
                    {/* --- Section 3: Additional Case Context --- */}
                    <DetailBox title="Case Description & Custom Fields">
                        <h4 className="font-bold text-md text-black mb-2 border-b pb-1">Internal Case Description:</h4>
                        <p className="text-gray-700 whitespace-pre-wrap text-sm">{caseData.description || 'No detailed internal description provided.'}</p>
                        
                        {Object.keys(custom_fields).length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-200">
                                <h4 className="font-bold text-md text-black mb-1">Custom Fields:</h4>
                                {Object.entries(custom_fields).map(([key, value]) => (
                                    <DetailItem key={key} label={key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} value={value} />
                                ))}
                            </div>
                        )}
                    </DetailBox>
                </div>

                {/* --- Action Sidebar --- */}
                <div className="lg:col-span-1 space-y-8">
                    
                    {/* Document Upload */}
                    {canUpload && (
                        <div className="bg-white p-5 rounded-lg shadow-xl border-t-4 border-green-500">
                            <h3 className="font-bold text-xl text-green-700 mb-4">Upload Proof of Service 📸</h3>
                            <DocumentUpload caseId={id} onUploadSuccess={handleActionSuccess} />
                        </div>
                    )}

                    {/* Documents and Media List */}
                    <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
                        <h3 className="font-semibold text-lg text-gray-800 mb-3 flex items-center space-x-2"><BiFile className="w-5 h-5 text-red-500" /> <span>Case Files ({documents.length + media.length})</span></h3>
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-bold text-sm text-gray-700 mb-2">Service Documents ({documents.length})</h4>
                                <ul className="space-y-2">
                                    {documents.length > 0 ? documents.map(doc => (<li key={doc.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md border border-gray-200"><span className="font-medium text-gray-700 text-sm truncate">{doc.file_name}</span><button onClick={() => handleDownload(doc.id, doc.file_name,'documents')} className="text-blue-600 hover:text-blue-800 font-semibold text-xs flex items-center space-x-1 ml-2 flex-shrink-0"><BiDownload className="w-4 h-4" /><span>Download</span></button></li>)) : (<p className="text-gray-500 text-xs italic">No official documents uploaded yet.</p>)}
                                </ul>
                            </div>
                            <div className="pt-3 border-t">
                                <h4 className="font-bold text-sm text-gray-700 mb-2">Proof Media ({media.length})</h4>
                                <ul className="space-y-2">
                                    {media.length > 0 ? media.map(mediaItem => (<li key={mediaItem.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-md border border-gray-200"><span className="font-medium text-gray-700 text-sm truncate">{mediaItem.file_name}</span><button onClick={() => handleDownload(mediaItem.id, mediaItem.file_name,'media')} className="text-blue-600 hover:text-blue-800 font-semibold text-xs flex items-center space-x-1 ml-2 flex-shrink-0"><BiDownload className="w-4 h-4" /><span>Download</span></button></li>)) : (<p className="text-gray-500 text-xs italic">No supporting media found.</p>)}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Activity Log */}
                    <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
                        <h3 className="font-semibold text-lg text-gray-800 mb-3">Activity Log ({activity.length})</h3>
                        <div className="max-h-96 overflow-y-auto space-y-3">
                            {activity.length > 0 ? activity.map(log => (<div key={log.id} className="p-3 bg-gray-50 rounded-md text-sm text-gray-700 border border-gray-200"><p className="font-medium">{log.description}</p><p className="text-xs text-gray-500 mt-1">{new Date(log.created_at).toLocaleString()}</p>{log.user_name && <p className="text-xs text-gray-500">By: {log.user_name}</p>}</div>)) : (<p className="text-gray-500 text-sm">No recent activity found.</p>)}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* --- Modals --- */}
            {showAttemptModal && (
                <ServiceAttemptRecorder 
                    caseId={id} 
                    servees={servees} 
                    onRecordSuccess={handleActionSuccess} 
                    onClose={() => setShowAttemptModal(false)} 
                />
            )}
        </div>
    );
};

export default MiniCaseDetail;